import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

// GET /public/{slug}/file/{id}/incentives

export const PublicFileIncentiveSchema = z.object({
  id: z.number(),
  name: z.string(),
  task_name: z.string().nullable(),
  resources: z.array(z.string().nullable()),
  points: z.number(),
  status: z.enum(["Pending", "Released"])
});

export type PublicFileIncentive = z.infer<typeof PublicFileIncentiveSchema>;

const ResponseSchema = z.array(PublicFileIncentiveSchema);

const RequestSchema = z.object({
  slug: z.string(),
  id: z.string()
});

type RequestType = z.infer<typeof RequestSchema>;

const QUERY_KEY = ["publicFileIncentives"] as const;

const fetcher = (params: RequestType) =>
  api<RequestType, PublicFileIncentive[]>({
    method: "GET",
    url: `/public/:slug/file/:id/incentives`,
    requestSchema: RequestSchema,
    responseSchema: ResponseSchema,
    body: params
  });

type UsePublicFileIncentivesOptions = {
  variables: RequestType;
  enabled?: boolean;
};

export function usePublicFileIncentives({
  variables,
  enabled = true
}: UsePublicFileIncentivesOptions) {
  return useQuery({
    queryKey: [...QUERY_KEY, variables] as const,
    queryFn: () => fetcher(variables),
    enabled
  });
}

export function usePublicFileIncentivesSuspense(variables: RequestType) {
  return useSuspenseQuery({
    queryKey: [...QUERY_KEY, variables],
    queryFn: () => fetcher(variables)
  });
}

export const publicFileIncentivesQuery = {
  queryKey: QUERY_KEY,
  fetcher
};
